"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, FileText, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Sparkles, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const stepsData = [{
  stepNumber: "01",
  title: "In-Depth Technical & Content Audit",
  subtitle: "Uncovering Hidden Bottlenecks",
  description: "I personally inspect your site structure, crawl errors, indexation issues, and content gaps. You get a raw, honest breakdown of what is holding your site back—not an automated PDF dump.",
  deliverable: "Custom technical roadmap & priority action items",
  icon: Search
}, {
  stepNumber: "02",
  title: "Strategic On-Page Optimization",
  subtitle: "Intent-Matched Architecture",
  description: "I map target keywords to high-intent pages, rewrite meta tags, optimize heading hierarchies, and enhance content depth to convert search traffic into real business inquiries.",
  deliverable: "Optimized landing pages & keyword targeting matrix",
  icon: FileText
}, {
  stepNumber: "03",
  title: "Authority & Citation Building",
  subtitle: "Clean Local & Industry Footprint",
  description: "I audit and submit accurate NAP (Name, Address, Phone) business citations across trusted UK and global directories, building authentic domain trust without risky spam links.",
  deliverable: "Consistent citation network & trustworthy backlink profile",
  icon: MapPin
}, {
  stepNumber: "04",
  title: "Review & Reputation Strategy",
  subtitle: "Maximizing Searcher Trust",
  description: "I optimize your Google Business Profile and establish review collection workflows to improve local map pack rankings and social proof that converts browsers into buyers.",
  deliverable: "Google Business Profile optimization & review funnel guide",
  icon: Star
}, {
  stepNumber: "05",
  title: "Advanced Schema & Structured Data",
  subtitle: "Rich Snippets & Entity Search",
  description: "I write and implement custom JSON-LD schema markup (Organization, Service, FAQ, LocalBusiness) so search engines and modern AI search engines accurately understand your business entities.",
  deliverable: "Validated custom JSON-LD schema implementation",
  icon: Code2
}, {
  stepNumber: "06",
  title: "Transparent Reporting & Strategy Calls",
  subtitle: "Measurable Business Growth",
  description: "Every month, you receive clear reporting focusing on rankings, organic traffic quality, and lead generation. We review results together on a dedicated 1-on-1 strategy call.",
  deliverable: "Custom live KPI dashboard & monthly consultation call",
  icon: BarChart3
}];
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".step-card", {
        y: 30
      });
      gsap.to(".step-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".step-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="stepbystepmethodology" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl" ref={containerRef}>
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground">
            <UserCheck className="w-3.5 h-3.5 mr-1 text-primary" />
            Direct Consultant Process
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight">
            My Step-by-Step <span className="text-primary">SEO Methodology</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you work with YE Consultancy, you work directly with me—Yassine
            Essaadi. Here is the exact, systematic framework I use to deliver sustainable search growth for your business.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-accent/30 -translate-y-1/2 z-0 rounded-full" />

          {}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
            {stepsData.map((step, i) => {
            const IconComponent = step.icon;
            return <div key={step.stepNumber} data-index={i} className="step-card flex flex-col h-full" style={{
              opacity: 0
            }}>
                  <Card className="h-full bg-card border-border shadow-sm hover:shadow-md transition-shadow duration-300 relative flex flex-col justify-between overflow-hidden group">
                    {}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity" />

                    <CardHeader className="pt-6 pb-4 px-6 relative">
                      <div className="flex items-center justify-between mb-4">
                        {}
                        <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center shadow-xs">
                          {step.stepNumber}
                        </div>

                        {}
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                          <IconComponent className="w-5 h-5" />
                        </div>
                      </div>

                      <div className="text-xs font-semibold uppercase tracking-wide text-accent mb-1">
                        {step.subtitle}
                      </div>

                      <CardTitle className="text-xl font-bold text-card-foreground font-serif leading-snug">
                        {step.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="px-6 pb-6 pt-0 flex-1 flex flex-col justify-between space-y-6">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>

                      {}
                      <div className="pt-4 border-t border-border mt-auto">
                        <div className="flex items-start gap-2 text-xs font-medium text-foreground bg-muted/60 p-3 rounded-lg">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          <span>
                            <strong className="text-primary font-semibold">Deliverable:</strong>{" "}
                            {step.deliverable}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-16 lg:mt-24 bg-card border border-border rounded-2xl p-8 sm:p-10 lg:p-12 shadow-sm text-center max-w-4xl mx-auto relative overflow-hidden">
          <div className="absolute -right-12 -top-12 w-40 h-40 bg-accent/10 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -left-12 -bottom-12 w-40 h-40 bg-primary/10 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 space-y-6">
            <div className="inline-flex items-center gap-2 text-accent font-medium text-sm">
              <Sparkles className="w-4 h-4" />
              <span>Tailored SEO Strategy Built For Your Business</span>
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
              Ready to Execute Step 1 for Your Website?
            </h3>

            <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
              Book a free consultation directly with me. We will evaluate your current rankings, discuss your business goals, and map out your strategic roadmap.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}